import type { ScheduleValuesJson, WorkDay } from "../types";
import type { ScheduleAmendmentFormValues } from "./validation";

export const WORK_DAYS: readonly WorkDay[] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const DEFAULT_WORK_DAYS: readonly WorkDay[] = ["Mon", "Tue", "Wed", "Thu", "Fri"];

export interface ScheduleLabels {
  breakLabel: string;
  noBreakLabel: string;
}

export function toHHMM(time: string | null | undefined): string {
  if (!time) return "";
  const parts = time.split(":");
  return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : time;
}

export function toMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

export function sortWorkDays(days: readonly WorkDay[]): WorkDay[] {
  return WORK_DAYS.filter((day) => days.includes(day));
}

export function buildScheduleNewValues(values: ScheduleAmendmentFormValues): ScheduleValuesJson {
  const newValues: ScheduleValuesJson = {
    working_days_list: sortWorkDays(values.working_days_list),
    work_start_time: values.work_start_time,
    work_end_time: values.work_end_time,
  };
  if (values.has_break) {
    newValues.break_start_time = values.break_start_time;
    newValues.break_end_time = values.break_end_time;
  } else {
    newValues.break_start_time = null;
    newValues.break_end_time = null;
  }
  return newValues;
}

export function buildScheduleAmendmentFormValues(
  values: ScheduleValuesJson | null | undefined,
  effectiveDate: Date | null,
): ScheduleAmendmentFormValues {
  const hasBreak = Boolean(values?.break_start_time && values?.break_end_time);
  return {
    effective_date: effectiveDate,
    working_days_list: values?.working_days_list?.length ? sortWorkDays(values.working_days_list) : [...DEFAULT_WORK_DAYS],
    work_start_time: toHHMM(values?.work_start_time),
    work_end_time: toHHMM(values?.work_end_time),
    has_break: hasBreak,
    break_start_time: hasBreak ? toHHMM(values?.break_start_time) : "",
    break_end_time: hasBreak ? toHHMM(values?.break_end_time) : "",
  };
}

export function isScheduleValuesJson(value: unknown): value is ScheduleValuesJson {
  return (
    typeof value === "object" &&
    value !== null &&
    ("work_start_time" in value || "work_end_time" in value || "working_days_list" in value)
  );
}

export function formatScheduleText(
  schedule: ScheduleValuesJson,
  weekdays: readonly string[],
  labels: ScheduleLabels,
): string {
  const days = sortWorkDays(schedule.working_days_list ?? [])
    .map((day) => weekdays[WORK_DAYS.indexOf(day)])
    .join(", ");
  const hasHours = schedule.work_start_time && schedule.work_end_time;
  const hours = hasHours ? `${toHHMM(schedule.work_start_time)}–${toHHMM(schedule.work_end_time)}` : "";
  const hasBreak = schedule.break_start_time && schedule.break_end_time;
  const breakText = hasBreak
    ? `${labels.breakLabel} ${toHHMM(schedule.break_start_time)}–${toHHMM(schedule.break_end_time)}`
    : hasHours
      ? labels.noBreakLabel
      : "";
  return [days, hours, breakText].filter(Boolean).join(", ");
}
